import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';
import { VariableBenefitPaymentFrequencyEnum } from '../../../../../../../models/enum/shared/variable-benefit-payment-frequency.enum';
import { VariableBenefitDestinationJurisdictionDetail } from '../../../../../../../models/submissions/variable-benefit-destination-jurisdiction-detail';
import { VbRecurringPaymentProrationUtils } from '../../../utils/vb-recurring-payment-proration.utils';
import {
  VbPaymentJurisdictionDetailsUtils,
  VbPaymentSubmissionData
} from '../../../utils/vb-payment-jurisdiction-details.utils';

type RecurringPaymentReviewData = VbPaymentSubmissionData;

interface RecurringPaymentRow {
  jurisdictionName: string;
  amount: number | null;
  paymentTypeLabel: string | null;
  isNew: boolean;
}

@Component({
  selector: 'app-update-vb-config-recurring-payment-review-card',
  templateUrl: './update-vb-config-recurring-payment-review-card.component.html',
  styleUrls: ['./update-vb-config-recurring-payment-review-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UpdateVbConfigRecurringPaymentReviewCardComponent implements OnChanges {
  @Input({ required: true }) data!: RecurringPaymentReviewData;
  @Input() showAsCurrentState = false;
  @Input() showAsNewState = false;
  @Input() subtitle: string | null = null;
  @Input() newJurisdictionKeys: string[] = [];
  @Input() estimatedAnnualTotal: number | null = null;

  public isMonthly = false;
  public rows: RecurringPaymentRow[] = [];
  public total = 0;
  public annualTotal = 0;

  public static jurisdictionKey(detail: VariableBenefitDestinationJurisdictionDetail): string {
    return detail.governingJurisdictionId !== null ? String(detail.governingJurisdictionId) : 'non-locked-in';
  }

  public ngOnChanges(): void {
    const details = VbPaymentJurisdictionDetailsUtils.paymentDetails(this.data ?? null);
    this.isMonthly = this.data?.recurringPaymentFrequencyId === VariableBenefitPaymentFrequencyEnum.Monthly;
    this.rows = details.map(d => this.toRow(d)).sort((a, b) => a.jurisdictionName.localeCompare(b.jurisdictionName));
    this.total = this.rows.reduce((sum, r) => sum + (r.amount ?? 0), 0);
    this.annualTotal =
      this.estimatedAnnualTotal ??
      this.total * VbRecurringPaymentProrationUtils.remainingPaymentMonths(this.data?.recurringPaymentStartDate);
  }

  private toRow(detail: VariableBenefitDestinationJurisdictionDetail): RecurringPaymentRow {
    const hasPayment = this.hasRecurringPayment(detail);
    return {
      jurisdictionName: detail.governingJurisdiction?.name ?? $localize`Non-Locked-In`,
      amount: hasPayment ? detail.recurringPaymentAmount ?? 0 : null,
      paymentTypeLabel: hasPayment ? this.resolvePaymentTypeLabel(detail) : null,
      isNew:
        this.showAsNewState &&
        this.newJurisdictionKeys.includes(UpdateVbConfigRecurringPaymentReviewCardComponent.jurisdictionKey(detail))
    };
  }

  private hasRecurringPayment(detail: VariableBenefitDestinationJurisdictionDetail): boolean {
    return (
      detail.recurringPaymentUseMinimumAmount ||
      detail.recurringPaymentUseMaximumAmount ||
      detail.recurringPaymentAmount !== null
    );
  }

  private resolvePaymentTypeLabel(detail: VariableBenefitDestinationJurisdictionDetail): string {
    if (detail.recurringPaymentUseMinimumAmount) {
      return $localize`Minimum`;
    }
    if (detail.recurringPaymentUseMaximumAmount) {
      return $localize`Maximum`;
    }
    return $localize`Custom`;
  }
}
